/**
 * Shared table design system — consistent across Academic, Assessments, Report Cards.
 * Exports: DataTable, ConfirmDialog, useAppSnackbar, useConfirmDialog, TABLE_SX, TH_SX, TD_SX
 */
import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from '@mui/material';

// ─── Shared sx constants ────────────────────────────────────────────────────

export const TABLE_SX = {
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider',
  overflowX: 'auto'
};

export const TH_SX = {
  bgcolor: 'grey.50',
  fontWeight: 700,
  whiteSpace: 'nowrap',
  borderBottom: '2px solid',
  borderColor: 'divider',
  py: 1.5
};

export const TD_SX = { py: 1.25 };

// ─── DataTable ───────────────────────────────────────────────────────────────
/**
 * @param {string[]} columns        - header labels
 * @param {function} renderRow      - (item, index) => <TableRow>
 * @param {Array}    data           - array of items to render
 * @param {boolean}  loading
 * @param {string}   emptyMessage
 * @param {number}   [minWidth=700]
 * @param {boolean}  [stickyHeader=true]
 * @param {number}   [maxHeight=560]
 * @param {object}   [paginationProps] - { count, page, rowsPerPage, onPageChange, onRowsPerPageChange }
 */
export const DataTable = ({
  columns,
  renderRow,
  data = [],
  loading,
  emptyMessage = 'No records found.',
  minWidth = 700,
  stickyHeader = true,
  maxHeight = 560,
  paginationProps
}) => (
  <Box>
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{ ...TABLE_SX, ...(stickyHeader ? { maxHeight } : {}) }}
    >
      <Table stickyHeader={stickyHeader} sx={{ minWidth }}>
        <TableHead>
          <TableRow>
            {columns.map((col, i) => (
              <TableCell key={i} sx={TH_SX}>{col}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={columns.length} align="center" sx={{ py: 8 }}>
                <CircularProgress size={28} />
              </TableCell>
            </TableRow>
          ) : data.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length} align="center" sx={{ py: 8 }}>
                <Typography color="text.secondary">{emptyMessage}</Typography>
              </TableCell>
            </TableRow>
          ) : (
            data.map((item, index) => renderRow(item, index))
          )}
        </TableBody>
      </Table>
    </TableContainer>
    {paginationProps && (
      <TablePagination
        component="div"
        rowsPerPageOptions={[10, 15, 25, 50]}
        {...paginationProps}
      />
    )}
  </Box>
);

// ─── ConfirmDialog ───────────────────────────────────────────────────────────
export const ConfirmDialog = ({ open, title = 'Confirm', message, onConfirm, onClose, severity = 'error' }) => (
  <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
    <DialogTitle>{title}</DialogTitle>
    <DialogContent>
      <Typography>{message}</Typography>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}>
      <Button onClick={onClose}>Cancel</Button>
      <Button onClick={onConfirm} variant="contained" color={severity}>Confirm</Button>
    </DialogActions>
  </Dialog>
);

// ─── useAppSnackbar ──────────────────────────────────────────────────────────
export const useAppSnackbar = () => {
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const showSnackbar = (message, severity = 'success') => setSnackbar({ open: true, message, severity });
  const closeSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));
  const SnackbarComponent = (
    <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={closeSnackbar}>
      <Alert severity={snackbar.severity} onClose={closeSnackbar} sx={{ width: '100%' }}>
        {snackbar.message}
      </Alert>
    </Snackbar>
  );
  return { showSnackbar, SnackbarComponent };
};

// ─── useConfirmDialog ────────────────────────────────────────────────────────
export const useConfirmDialog = () => {
  const [state, setState] = useState({ open: false, title: '', message: '', onConfirm: null, severity: 'error' });
  const confirm = ({ title, message, onConfirm, severity = 'error' }) =>
    setState({ open: true, title, message, onConfirm, severity });
  const close = () => setState((prev) => ({ ...prev, open: false }));
  const ConfirmDialogComponent = (
    <ConfirmDialog
      open={state.open}
      title={state.title}
      message={state.message}
      onConfirm={() => { close(); state.onConfirm?.(); }}
      onClose={close}
      severity={state.severity}
    />
  );
  return { confirm, ConfirmDialogComponent };
};
